import { useState, useEffect, useCallback } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertCircle,
  AlertTriangle,
  Archive,
  CheckCircle2,
  ChevronRight,
  Clock,
  Copy,
  ExternalLink,
  Filter,
  Flame,
  Globe,
  Info,
  Loader2,
  RefreshCw,
  Search,
  Server,
  Shield,
  Trash2,
  User,
  X,
  XCircle,
} from "lucide-react";

// ─── Types ──────────────────────────────────────────────────────────────────────

type IssueAction = "resolve" | "unresolve" | "ignore" | "archive" | "delete";

interface SentryIssue {
  id: string;
  title: string;
  culprit?: string | null;
  permalink?: string | null;
  shortId?: string;
  status: string;
  level: string;
  count?: string;
  userCount?: number;
  firstSeen: string;
  lastSeen: string;
  project?: { id: string; slug: string; name: string } | null;
  metadata?: { value?: string; filename?: string; type?: string } | null;
  tags?: { key: string; value: string }[] | null;
  isUnhandled?: boolean;
  platform?: string;
  latestEvent?: SentryEvent | null;
}

interface SentryEvent {
  id: string;
  message?: string | null;
  title?: string;
  platform?: string;
  dateCreated?: string;
  tags?: { key: string; value: string }[];
  entries?: { type: string; data: unknown }[];
  user?: { id?: string; email?: string; username?: string; ipAddress?: string } | null;
  contexts?: Record<string, unknown> | null;
  sdk?: { name?: string; version?: string } | null;
  request?: {
    url?: string;
    method?: string;
    headers?: unknown;
    data?: unknown;
    queryString?: unknown;
  } | null;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function levelBadgeClass(level: string): string {
  switch (level) {
    case "fatal":   return "bg-red-100 text-red-700 border-red-200";
    case "error":   return "bg-red-50 text-red-600 border-red-200";
    case "warning": return "bg-amber-50 text-amber-600 border-amber-200";
    case "info":    return "bg-blue-50 text-blue-600 border-blue-200";
    default:        return "bg-muted text-muted-foreground border-border";
  }
}

function levelIconClass(level: string): string {
  switch (level) {
    case "fatal":   return "text-red-700";
    case "error":   return "text-red-600";
    case "warning": return "text-amber-500";
    case "info":    return "text-blue-500";
    default:        return "text-muted-foreground";
  }
}

function LevelIcon({ level, className = "w-4 h-4" }: { level: string; className?: string }) {
  switch (level) {
    case "fatal":   return <Flame className={className} />;
    case "error":   return <XCircle className={className} />;
    case "warning": return <AlertTriangle className={className} />;
    case "info":    return <Info className={className} />;
    default:        return <AlertCircle className={className} />;
  }
}

function relativeTime(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString("de-DE", {
    day: "2-digit", month: "2-digit", year: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

function extractStackTrace(event: SentryEvent | null | undefined): string | null {
  if (!event?.entries) return null;
  const ex = event.entries.find(e => e.type === "exception");
  if (!ex) return null;
  const data = ex.data as any;
  const values: any[] = data?.values ?? [];
  const lines: string[] = [];
  for (const val of values) {
    if (val?.type) lines.push(`${val.type}: ${val.value ?? ""}`);
    const frames: any[] = val?.stacktrace?.frames ?? [];
    for (let i = frames.length - 1; i >= 0; i--) {
      const f = frames[i];
      const loc = [f.filename, f.lineno ? `:${f.lineno}` : "", f.colno ? `:${f.colno}` : ""].join("");
      const inApp = f.in_app !== false;
      lines.push(`  ${inApp ? "▶" : "·"} ${f.function ?? "<anonymous>"} @ ${loc}`);
      if (f.context_line && inApp) lines.push(`    ${f.context_line.trim()}`);
    }
  }
  return lines.length ? lines.join("\n") : null;
}

// ─── Summary cards ─────────────────────────────────────────────────────────────

function SummaryCards() {
  // @ts-ignore
  const { data, isLoading } = trpc.sentry.stats.useQuery(undefined, { refetchInterval: 60_000 });

  const cards = [
    { label: "Unresolved", value: (data as any)?.totalUnresolved ?? 0, colorClass: "text-red-600",         Icon: XCircle },
    { label: "Fatal",      value: (data as any)?.fatalCount ?? 0,       colorClass: "text-red-800",         Icon: Flame },
    { label: "Last 24h",   value: (data as any)?.last24hCount ?? 0,     colorClass: "text-amber-600",       Icon: Clock },
    { label: "Frontend",   value: (data as any)?.frontendCount ?? 0,    colorClass: "text-primary",         Icon: Globe },
    { label: "Backend",    value: (data as any)?.backendCount ?? 0,     colorClass: "text-muted-foreground", Icon: Server },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {cards.map(({ label, value, colorClass, Icon }) => (
        <Card key={label} className="border-border">
          <CardContent className="p-4 flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <Icon className={`w-4 h-4 ${colorClass}`} />
              {isLoading && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
            </div>
            <div className={`text-2xl font-light ${colorClass}`}>{value}</div>
            <div className="text-xs text-muted-foreground">{label}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── Issue row ──────────────────────────────────────────────────────────────────

function IssueRow({ issue, selected, onClick }: { issue: SentryIssue; selected: boolean; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      className={`flex items-start gap-3 px-3 py-3 cursor-pointer border-b border-border transition-colors ${
        selected ? "bg-primary/5" : "hover:bg-muted/50"
      }`}
    >
      <div className={`flex-shrink-0 mt-0.5 ${levelIconClass(issue.level)}`}>
        <LevelIcon level={issue.level} className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-normal truncate text-foreground">{issue.title}</div>
        {issue.culprit && (
          <div className="text-xs truncate font-mono mt-0.5 text-muted-foreground">{issue.culprit}</div>
        )}
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 font-normal ${levelBadgeClass(issue.level)}`}>
            {issue.level}
          </Badge>
          {issue.project && (
            <span className="text-[10px] text-muted-foreground">{issue.project.slug}</span>
          )}
          <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
            <Clock className="w-2.5 h-2.5" />
            {relativeTime(issue.lastSeen)}
          </span>
          {issue.count && (
            <span className="text-[10px] font-mono text-muted-foreground">{issue.count}×</span>
          )}
        </div>
      </div>
      <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 mt-1 text-muted-foreground" />
    </div>
  );
}

// ─── Action button with two-step confirmation ───────────────────────────────────

function ActionButton({
  label,
  confirmLabel = "Bestätigen?",
  icon: Icon,
  action,
  pending,
  disabled,
  variant = "default",
  onFirstClick,
  onConfirm,
  onCancel,
}: {
  label: string;
  confirmLabel?: string;
  icon: typeof CheckCircle2;
  action: IssueAction;
  pending: IssueAction | null;
  disabled: boolean;
  variant?: "default" | "danger" | "success";
  onFirstClick: (a: IssueAction) => void;
  onConfirm: (a: IssueAction) => void;
  onCancel: () => void;
}) {
  const isConfirming = pending === action;

  const baseClass = "h-7 text-xs px-2.5 gap-1 transition-all";
  const variantClass = isConfirming
    ? "bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100"
    : variant === "danger"
      ? "border-border text-red-500 hover:bg-red-50 hover:border-red-200"
      : variant === "success"
        ? "border-border text-primary hover:bg-primary/5"
        : "border-border text-muted-foreground hover:text-foreground hover:bg-muted";

  return (
    <Button
      size="sm"
      variant="outline"
      className={`${baseClass} ${variantClass}`}
      disabled={disabled}
      onClick={() => { if (isConfirming) onConfirm(action); else onFirstClick(action); }}
    >
      <Icon className="w-3 h-3" />
      {isConfirming ? confirmLabel : label}
      {isConfirming && (
        <button
          className="ml-1 text-[10px] underline text-muted-foreground"
          onClick={e => { e.stopPropagation(); onCancel(); }}
        >
          abbrechen
        </button>
      )}
    </Button>
  );
}

// ─── Issue detail panel ─────────────────────────────────────────────────────────

function IssueDetail({ issueId, onAction }: { issueId: string; onAction: (id: string, action: IssueAction) => void }) {
  const [copiedStack, setCopiedStack] = useState(false);
  const [pendingAction, setPendingAction] = useState<IssueAction | null>(null);

  // @ts-ignore
  const { data: issueData, isLoading } = trpc.sentry.getIssue.useQuery(
    { issueId },
    { refetchOnWindowFocus: false }
  );

  // @ts-ignore
  const mutation = trpc.sentry.updateIssue.useMutation({
    onSuccess: (_: any, vars: any) => {
      toast.success(`Issue ${vars.action}d`);
      onAction(vars.issueId, vars.action);
      setPendingAction(null);
    },
    onError: (err: any) => {
      toast.error(err.message ?? "Aktion fehlgeschlagen");
      setPendingAction(null);
    },
  });

  const issue = issueData as SentryIssue & { latestEvent?: SentryEvent } | undefined;
  const event = issue?.latestEvent ?? null;
  const stackTrace = extractStackTrace(event);

  const handleCopyStack = () => {
    if (stackTrace) {
      navigator.clipboard.writeText(stackTrace).then(() => {
        setCopiedStack(true);
        setTimeout(() => setCopiedStack(false), 2000);
      });
    }
  };

  const handleConfirm = (action: IssueAction) => {
    if (!issue) return;
    mutation.mutate({ issueId: issue.id, action });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full py-20">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!issue) return null;

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Header */}
      <div className="p-4 border-b border-border bg-card sticky top-0 z-10">
        {/* Title */}
        <div className="flex items-start gap-2 mb-3">
          <div className={`flex-shrink-0 mt-0.5 ${levelIconClass(issue.level)}`}>
            <LevelIcon level={issue.level} className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium leading-snug text-foreground">{issue.title}</h3>
            {issue.shortId && (
              <span className="text-[10px] font-mono text-muted-foreground">{issue.shortId}</span>
            )}
          </div>
        </div>

        {/* Badges */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          <Badge variant="outline" className={`text-[10px] font-normal ${levelBadgeClass(issue.level)}`}>
            {issue.level}
          </Badge>
          <Badge variant="outline" className="text-[10px] font-normal border-border text-muted-foreground">
            {issue.status}
          </Badge>
          {issue.project && (
            <Badge variant="outline" className="text-[10px] font-normal border-border text-muted-foreground">
              {issue.project.name}
            </Badge>
          )}
          {issue.isUnhandled && (
            <Badge variant="outline" className="text-[10px] font-normal bg-red-50 text-red-600 border-red-200">
              unhandled
            </Badge>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-1.5">
          {issue.status !== "resolved" && (
            <ActionButton
              label="Resolve" icon={CheckCircle2} action="resolve" variant="success"
              pending={pendingAction} disabled={mutation.isPending}
              onFirstClick={setPendingAction} onConfirm={handleConfirm} onCancel={() => setPendingAction(null)}
            />
          )}
          {issue.status === "resolved" && (
            <ActionButton
              label="Reopen" icon={RefreshCw} action="unresolve"
              pending={pendingAction} disabled={mutation.isPending}
              onFirstClick={setPendingAction} onConfirm={handleConfirm} onCancel={() => setPendingAction(null)}
            />
          )}
          {issue.status !== "ignored" && (
            <ActionButton
              label="Ignore" icon={X} action="ignore"
              pending={pendingAction} disabled={mutation.isPending}
              onFirstClick={setPendingAction} onConfirm={handleConfirm} onCancel={() => setPendingAction(null)}
            />
          )}
          <ActionButton
            label="Archive" icon={Archive} action="archive"
            pending={pendingAction} disabled={mutation.isPending}
            onFirstClick={setPendingAction} onConfirm={handleConfirm} onCancel={() => setPendingAction(null)}
          />
          <ActionButton
            label="Delete" confirmLabel="Löschen?" icon={Trash2} action="delete" variant="danger"
            pending={pendingAction} disabled={mutation.isPending}
            onFirstClick={setPendingAction} onConfirm={handleConfirm} onCancel={() => setPendingAction(null)}
          />
          {issue.permalink && (
            <a href={issue.permalink} target="_blank" rel="noopener noreferrer">
              <Button size="sm" variant="outline" className="h-7 text-xs px-2.5 gap-1 border-border text-muted-foreground hover:text-foreground">
                <ExternalLink className="w-3 h-3" /> Sentry
              </Button>
            </a>
          )}
        </div>
      </div>

      {/* Meta grid */}
      <div className="px-4 py-3 border-b border-border bg-muted/30">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground">Events:</span>
            <span className="font-mono font-medium text-foreground">{issue.count ?? "—"}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground">Users:</span>
            <span className="font-mono font-medium text-foreground">{issue.userCount ?? "—"}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground">First seen:</span>
            <span className="text-foreground">{fmtDate(issue.firstSeen)}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground">Last seen:</span>
            <span className="text-foreground">{fmtDate(issue.lastSeen)}</span>
          </div>
          {issue.culprit && (
            <div className="col-span-2 flex items-start gap-1.5">
              <span className="text-muted-foreground shrink-0">Culprit:</span>
              <span className="font-mono break-all text-foreground">{issue.culprit}</span>
            </div>
          )}
        </div>
      </div>

      {/* Event details */}
      {event ? (
        <div className="p-4 space-y-4 text-xs">
          {/* Message */}
          {(event.message || issue.metadata?.value) && (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground mb-1.5">Message</div>
              <div className="rounded-md p-3 font-mono text-xs break-words bg-muted text-foreground border border-border">
                {event.message ?? issue.metadata?.value}
              </div>
            </div>
          )}

          {/* Stack trace */}
          {stackTrace && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground">Stack Trace</div>
                <Button
                  size="sm" variant="ghost"
                  className={`h-6 text-[10px] px-1.5 gap-1 ${copiedStack ? "text-primary" : "text-muted-foreground"}`}
                  onClick={handleCopyStack}
                >
                  <Copy className="w-2.5 h-2.5" />
                  {copiedStack ? "Kopiert!" : "Kopieren"}
                </Button>
              </div>
              <pre className="rounded-md p-3 text-[10px] leading-relaxed overflow-auto max-h-64 font-mono whitespace-pre-wrap break-all bg-zinc-900 text-zinc-200">
                {stackTrace}
              </pre>
            </div>
          )}

          {/* Request */}
          {event.request?.url && (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground mb-1.5">Request</div>
              <div className="rounded-md p-3 bg-muted border border-border text-foreground">
                <span className="font-semibold text-primary">{event.request.method ?? "GET"}</span>{" "}
                <span className="font-mono break-all">{event.request.url}</span>
              </div>
            </div>
          )}

          {/* User */}
          {event.user && (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground mb-1.5">User</div>
              <div className="flex items-center gap-2 rounded-md p-3 bg-muted border border-border">
                <User className="w-3.5 h-3.5 flex-shrink-0 text-primary" />
                <div className="text-foreground flex items-center gap-1.5 flex-wrap">
                  {event.user.email && <span>{event.user.email}</span>}
                  {event.user.username && !event.user.email && <span>{event.user.username}</span>}
                  {event.user.id && <span className="font-mono text-[10px] text-muted-foreground">#{event.user.id}</span>}
                  {event.user.ipAddress && <span className="font-mono text-[10px] text-muted-foreground">{event.user.ipAddress}</span>}
                </div>
              </div>
            </div>
          )}

          {/* Tags */}
          {event.tags && event.tags.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground mb-1.5">Tags</div>
              <div className="flex flex-wrap gap-1.5">
                {event.tags.map(tag => (
                  <span
                    key={`${tag.key}:${tag.value}`}
                    className="inline-flex items-center rounded-md px-2 py-0.5 text-[10px] bg-primary/8 border border-primary/15 text-foreground"
                  >
                    <span className="text-muted-foreground">{tag.key}:</span>
                    <span className="font-medium ml-0.5 text-primary">{tag.value}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* SDK */}
          {event.sdk?.name && (
            <div className="text-[10px] text-muted-foreground">
              SDK: {event.sdk.name} {event.sdk.version}
            </div>
          )}
        </div>
      ) : (
        <div className="p-4 text-xs text-center text-muted-foreground py-12">
          Keine Event-Details verfügbar
        </div>
      )}
    </div>
  );
}

// ─── Not configured banner ──────────────────────────────────────────────────────

function NotConfiguredBanner() {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-12 h-12 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center mb-4">
        <Shield className="w-6 h-6 text-amber-500" />
      </div>
      <div className="text-base font-display font-normal text-foreground mb-2">Sentry nicht konfiguriert</div>
      <div className="text-xs text-muted-foreground max-w-sm leading-relaxed">
        Füge <code className="bg-muted px-1.5 py-0.5 rounded text-foreground font-mono">SENTRY_API_TOKEN</code> und{" "}
        <code className="bg-muted px-1.5 py-0.5 rounded text-foreground font-mono">SENTRY_ORG</code> zu deinen Umgebungsvariablen hinzu.
      </div>
    </div>
  );
}

// ─── Empty state ────────────────────────────────────────────────────────────────

function EmptyState({ filtered }: { filtered: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-12 h-12 rounded-full bg-green-50 border border-green-200 flex items-center justify-center mb-4">
        <CheckCircle2 className="w-6 h-6 text-green-500" />
      </div>
      <div className="text-base font-display font-normal text-foreground mb-1">
        {filtered ? "Keine passenden Issues" : "Alles sauber!"}
      </div>
      <div className="text-xs text-muted-foreground">
        {filtered ? "Filter anpassen, um mehr zu sehen." : "Sentry verfolgt keine Issues in diesem Zustand."}
      </div>
    </div>
  );
}

// ─── Main dashboard ─────────────────────────────────────────────────────────────

export default function AppAdminSentry() {
  const [project, setProject] = useState<"all" | "frontend" | "backend">("all");
  const [status, setStatus]   = useState<"unresolved" | "resolved" | "ignored" | "all">("unresolved");
  const [level, setLevel]     = useState<"error" | "warning" | "info" | "debug" | "fatal" | "all">("all");
  const [query, setQuery]     = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 400);
    return () => clearTimeout(t);
  }, [query]);

  // @ts-ignore
  const { data: config, isLoading: configLoading } = trpc.sentry.isConfigured.useQuery();

  // @ts-ignore
  const { data, isLoading, refetch } = trpc.sentry.list.useQuery(
    { project, status, level, query: debouncedQuery || undefined },
    { refetchInterval: 60_000, enabled: !!(config as any)?.configured }
  );

  const issues: SentryIssue[] = ((data as any)?.issues as SentryIssue[] | undefined) ?? [];

  useEffect(() => {
    if (issues.length > 0 && !selectedId) setSelectedId(issues[0].id);
  }, [issues.length]);

  useEffect(() => {
    if (selectedId && issues.length > 0 && !issues.find(i => i.id === selectedId)) {
      setSelectedId(issues[0]?.id ?? null);
    }
  }, [issues, selectedId]);

  const handleAction = useCallback(
    (_issueId: string, _action: IssueAction) => {
      refetch();
      if (_action === "delete" || _action === "resolve" || _action === "ignore" || _action === "archive") {
        setSelectedId(null);
      }
    },
    [refetch]
  );


  const isFiltered = status !== "unresolved" || level !== "all" || !!debouncedQuery || project !== "all";

  if (configLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!(config as any)?.configured) {
    return <NotConfiguredBanner />;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-display font-normal text-foreground">Fehler-Tracking</h2>
          <p className="text-xs mt-0.5 text-muted-foreground">Live error monitoring · Frontend &amp; Backend</p>
        </div>
        <Button
          variant="outline" size="sm"
          className="h-8 text-xs gap-1.5 border-border text-muted-foreground hover:text-foreground"
          onClick={() => refetch()}
          disabled={isLoading}
        >
          {isLoading
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <SummaryCards />

      {/* Filter bar */}
      <div className="flex items-center gap-2 p-2.5 rounded-lg border border-border bg-card overflow-x-auto">
        <Filter className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground" />

        <Select value={project} onValueChange={v => setProject(v as typeof project)}>
          <SelectTrigger className="w-32 h-8 text-xs shrink-0 border-border">
            <SelectValue placeholder="Projekt" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Projekte</SelectItem>
            {(config as any)?.hasFrontendProject && <SelectItem value="frontend">Frontend</SelectItem>}
            {(config as any)?.hasBackendProject && <SelectItem value="backend">Backend</SelectItem>}
          </SelectContent>
        </Select>

        <Select value={status} onValueChange={v => setStatus(v as typeof status)}>
          <SelectTrigger className="w-32 h-8 text-xs shrink-0 border-border">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="unresolved">Unresolved</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="ignored">Ignored</SelectItem>
            <SelectItem value="all">Alle Status</SelectItem>
          </SelectContent>
        </Select>

        <Select value={level} onValueChange={v => setLevel(v as typeof level)}>
          <SelectTrigger className="w-28 h-8 text-xs shrink-0 border-border">
            <SelectValue placeholder="Level" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Level</SelectItem>
            <SelectItem value="fatal">Fatal</SelectItem>
            <SelectItem value="error">Error</SelectItem>
            <SelectItem value="warning">Warning</SelectItem>
            <SelectItem value="info">Info</SelectItem>
          </SelectContent>
        </Select>

        <div className="relative flex-1 min-w-32">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
          <Input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Issues suchen…"
            className="pl-8 h-8 text-xs border-border"
          />
        </div>
      </div>

      {/* Split-pane */}
      <div
        className="flex rounded-lg border border-border overflow-hidden"
        style={{ minHeight: "calc(100vh - 360px)" }}
      >
        {/* Issue list */}
        <div
          className="border-r border-border overflow-y-auto bg-card"
          style={{
            width: selectedId ? "38%" : "100%",
            transition: "width 0.2s ease",
            flexShrink: 0,
          }}
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : issues.length === 0 ? (
            <EmptyState filtered={isFiltered} />
          ) : (
            issues.map(issue => (
              <IssueRow
                key={issue.id}
                issue={issue}
                selected={selectedId === issue.id}
                onClick={() => setSelectedId(selectedId === issue.id ? null : issue.id)}
              />
            ))
          )}
        </div>

        {/* Detail panel */}
        {selectedId && (
          <div className="flex-1 overflow-hidden bg-card">
            <IssueDetail issueId={selectedId} onAction={handleAction} />
          </div>
        )}
      </div>

      {!isLoading && issues.length > 0 && (
        <div className="text-xs text-right text-muted-foreground">
          {issues.length} Issue{issues.length !== 1 ? "s" : ""}
        </div>
      )}
    </div>
  );
}
